/**
 * Finance › Aged debt, `/finance/aged-debt` and `/finance/aged-debt/disputes`
 * (docs/features/v1-completion/L_credit_control_spec.md §3.3, §3.4, §4.7; mock-up
 * docs/features/v1-completion/mockups/L_credit_control.html states 1 to 5).
 *
 * Two tabs over the CIS entity's credit-account invoices (Sage and Tharstern debt is not here):
 * - Aged debt: one row per payer that owes, by days overdue, with the filtered set's totals; a payer opens its own
 *   open invoices under its row, where finance records a dispute.
 * - Disputes: every dispute recorded, open or resolved, with Resolve.
 *
 * Search, filter, sort and paging run on the server (`listAgedDebt`, `listDisputes`; docs/list-endpoints.md); this
 * page holds them in the URL, so a filtered list is a link, and changing any of them goes back to page 1. The write
 * buttons show only where a row carries the URL to post to (credit_control:manage).
 */
import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { AgedDebtListOptions } from "@shared-core/data/adapter";
import type {
  AgedDebtList,
  AgedDebtPayer,
  DisputeList,
  DisputeRow,
  DisputeStatus,
} from "@shared-core/data/aged-debt-types";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import {
  AGED_DEBT_TABS,
  paths,
  type AgedDebtTab,
} from "@shared-core/routing/routes";
import { AgedDebtInvoicesPanel } from "../app/components/domain/credit-control/AgedDebtInvoicesPanel";
import { AgedDebtPayerTable } from "../app/components/domain/credit-control/AgedDebtPayerTable";
import { ResolveDisputeDialog } from "../app/components/domain/credit-control/DisputeDialogs";
import { DisputesTable } from "../app/components/domain/credit-control/DisputesTable";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Badge } from "../app/components/primitives/badge";
import { Checkbox } from "../app/components/primitives/checkbox";
import { Input } from "../app/components/primitives/input";
import { Label } from "../app/components/primitives/label";
import { Skeleton } from "../app/components/primitives/skeleton";
import { cn } from "../app/components/primitives/utils";

const PAGE_SIZE = 25;
const selectClass = "h-9 rounded-md border border-border bg-card px-3 text-sm";

const TAB_LABELS: Record<AgedDebtTab, string> = {
  "aged-debt": "Aged debt",
  disputes: "Disputes",
};

// ── The URL's filters ─────────────────────────────────────────────────────

const OVERDUE_OPTIONS = [
  { value: "30", label: "Oldest is 30+ days late" },
  { value: "60", label: "Oldest is 60+ days late" },
  { value: "90", label: "Oldest is 90+ days late" },
] as const;
type OverdueValue = (typeof OVERDUE_OPTIONS)[number]["value"];

const PAYER_SORT_OPTIONS = [
  { value: "-overdue_pence", label: "Most overdue" },
  { value: "-outstanding_pence", label: "Most owed" },
  { value: "-oldest_overdue_days", label: "Oldest invoice" },
  { value: "-disputed_pence", label: "Most disputed" },
  { value: "-credit_limit_pence", label: "Largest limit" },
  { value: "payer_name", label: "Payer A–Z" },
] as const;
type PayerSort = (typeof PAYER_SORT_OPTIONS)[number]["value"];

const STATUS_OPTIONS: { value: DisputeStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
];

const DISPUTE_SORT_OPTIONS = [
  { value: "-raised_at", label: "Newest first" },
  { value: "raised_at", label: "Oldest first" },
  { value: "payer_name", label: "Payer A–Z" },
  { value: "invoice_no", label: "Invoice number" },
] as const;
type DisputeSort = (typeof DISPUTE_SORT_OPTIONS)[number]["value"];

/** A filter the URL holds as a value from a fixed list; anything else is no filter. */
function oneOf<T extends string>(
  options: readonly { value: T }[],
  value: string | null,
): T | undefined {
  return options.find((o) => o.value === value)?.value;
}

function useUrlFilters() {
  const [params, setParams] = useSearchParams();
  const setFilter = (key: string, value: string | null) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== "page") next.delete("page");
        return next;
      },
      { replace: true },
    );
  const page = Math.max(1, Number(params.get("page")) || 1);
  return { params, setFilter, page };
}

/** The search box is typed into freely and written to the URL once it settles. */
function useSearchBox(
  q: string | undefined,
  setFilter: (key: string, value: string | null) => void,
) {
  const [query, setQuery] = useState(q ?? "");
  const debounced = useDebouncedValue(query, 300).trim();
  useEffect(() => {
    if (debounced !== (q ?? "")) setFilter("q", debounced || null);
    // The URL's own q is the other direction; only a settled box writes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  return [query, setQuery] as const;
}

type ListState<T> = {
  /** The last page that arrived: kept on screen while the next loads. */
  list: T | null;
  isLoading: boolean;
  error: string | null;
};

function useServerList<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
  failure: string,
): ListState<T> {
  const [state, setState] = useState<ListState<T>>({
    list: null,
    isLoading: true,
    error: null,
  });
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true, error: null }));
    load()
      .then((list) => {
        if (!cancelled) setState({ list, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setState((s) => ({
            ...s,
            isLoading: false,
            error: err instanceof Error ? err.message : failure,
          }));
      });
    return () => {
      cancelled = true;
    };
    // `load` closes over `deps`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

// ── The page ──────────────────────────────────────────────────────────────

export function AgedDebtPage() {
  const adapter = useDataAdapter();
  const { tab: rawTab } = useParams();
  const tab: AgedDebtTab | null = !rawTab
    ? "aged-debt"
    : (AGED_DEBT_TABS as readonly string[]).includes(rawTab)
      ? (rawTab as AgedDebtTab)
      : null;

  // Bumped whenever a dispute is recorded or resolved: both tabs and the open count re-read.
  const [reloads, setReloads] = useState(0);
  const [asOf, setAsOf] = useState<string | null>(null);
  const openDisputes = useServerList(
    () => adapter.listDisputes({ status: "open", page: 1, pageSize: 1 }),
    [adapter, reloads],
    "The open disputes did not load.",
  );
  const openCount = openDisputes.list?.total ?? null;

  return (
    <div className="flex h-full flex-col">
      <header className="px-6 pt-6 pb-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h1 className="text-foreground text-2xl">Aged debt</h1>
          <span className="text-muted-foreground text-xs">
            {asOf ? `As of ${formatDateOrDash(asOf)} · ` : ""}
            Only invoices raised in One
          </span>
        </div>
        <nav aria-label="Aged debt" className="mt-3 flex gap-1 border-b">
          {AGED_DEBT_TABS.map((t) => (
            <Link
              key={t}
              to={paths.financeAgedDebt({ tab: t })}
              aria-current={t === tab ? "page" : undefined}
              className={cn(
                "-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition-colors",
                t === tab
                  ? "border-primary text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground border-transparent",
              )}
            >
              {TAB_LABELS[t]}
              {t === "disputes" && openCount !== null && openCount > 0 && (
                <Badge variant="neutral" className="font-normal">
                  {openCount} open
                </Badge>
              )}
            </Link>
          ))}
        </nav>
      </header>

      {tab === "aged-debt" && (
        <AgedDebtTabBody
          reloads={reloads}
          onDisputesChanged={() => setReloads((n) => n + 1)}
          onAsOf={setAsOf}
        />
      )}
      {tab === "disputes" && (
        <DisputesTabBody
          reloads={reloads}
          onDisputesChanged={() => setReloads((n) => n + 1)}
          onAsOf={setAsOf}
        />
      )}
      {tab === null && (
        <p className="text-muted-foreground px-6 py-6 text-sm">
          The Aged debt page has no “{rawTab}” tab.
        </p>
      )}
    </div>
  );
}

function FilterBar({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-6 py-3">
      {children}
    </div>
  );
}

function Loading({ label }: { label: string }) {
  return (
    <div className="space-y-2 p-6" aria-label={label}>
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

function Pager({
  summary,
  list,
  onPageChange,
}: {
  summary: string;
  list: { page: number; totalPages: number };
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-3">
      <span className="text-muted-foreground text-xs tabular-nums">
        {summary}
      </span>
      <ListPagination
        page={list.page}
        totalPages={list.totalPages}
        onPageChange={onPageChange}
      />
    </div>
  );
}

// ── Aged debt tab ─────────────────────────────────────────────────────────

function AgedDebtTabBody({
  reloads,
  onDisputesChanged,
  onAsOf,
}: {
  reloads: number;
  onDisputesChanged: () => void;
  onAsOf: (asOf: string) => void;
}) {
  const adapter = useDataAdapter();
  const { params, setFilter, page } = useUrlFilters();
  const q = params.get("q") || undefined;
  const overdue: OverdueValue | undefined = oneOf(
    OVERDUE_OPTIONS,
    params.get("overdue"),
  );
  const onStop = params.get("on_stop") === "true";
  const disputed = params.get("disputed") === "true";
  const mine = params.get("mine") === "true";
  const sort: PayerSort | undefined = oneOf(
    PAYER_SORT_OPTIONS,
    params.get("sort"),
  );
  const isFiltered = Boolean(q || overdue || onStop || disputed || mine);
  const [query, setQuery] = useSearchBox(q, setFilter);
  const [openRef, setOpenRef] = useState<string | null>(null);

  const options: AgedDebtListOptions = {
    q,
    overdue,
    onStop: onStop ? true : undefined,
    disputed: disputed ? true : undefined,
    mine: mine ? true : undefined,
    sort,
    page,
    pageSize: PAGE_SIZE,
  };
  const { list, isLoading, error } = useServerList<AgedDebtList>(
    () => adapter.listAgedDebt(options),
    [adapter, q, overdue, onStop, disputed, mine, sort, page, reloads],
    "The aged debt did not load.",
  );
  useEffect(() => {
    if (list) onAsOf(list.asOf);
  }, [list, onAsOf]);

  const sortLabel =
    PAYER_SORT_OPTIONS.find((o) => o.value === (sort ?? "-overdue_pence"))
      ?.label ?? "Most overdue";
  const openPayer: AgedDebtPayer | undefined = list?.items.find(
    (p) => p.billing_account_ref === openRef,
  );

  return (
    <>
      <FilterBar>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Payer name or code"
          aria-label="Search payers"
          className="h-9 w-full sm:w-72"
        />
        <select
          aria-label="Overdue"
          value={overdue ?? ""}
          onChange={(e) => setFilter("overdue", e.target.value || null)}
          className={selectClass}
        >
          <option value="">Any</option>
          {OVERDUE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="flex items-center gap-2">
          <Checkbox
            id="aged-on-stop"
            checked={onStop}
            onCheckedChange={(c) => setFilter("on_stop", c ? "true" : null)}
          />
          <Label htmlFor="aged-on-stop" className="font-normal">
            On stop
          </Label>
        </span>
        <span className="flex items-center gap-2">
          <Checkbox
            id="aged-disputed"
            checked={disputed}
            onCheckedChange={(c) => setFilter("disputed", c ? "true" : null)}
          />
          <Label htmlFor="aged-disputed" className="font-normal">
            Has a dispute
          </Label>
        </span>
        <span className="flex items-center gap-2">
          <Checkbox
            id="aged-mine"
            checked={mine}
            onCheckedChange={(c) => setFilter("mine", c ? "true" : null)}
          />
          <Label htmlFor="aged-mine" className="font-normal">
            My customers
          </Label>
        </span>
        <select
          aria-label="Sort payers"
          value={sort ?? ""}
          onChange={(e) => setFilter("sort", e.target.value || null)}
          className={cn(selectClass, "sm:ml-auto")}
        >
          <option value="">Sort…</option>
          {PAYER_SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </FilterBar>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error && (
          <p role="alert" className="p-6 text-sm text-red-600">
            Could not load the aged debt: {error}
          </p>
        )}
        {!list && isLoading && <Loading label="Loading the aged debt" />}
        {list &&
          (list.items.length === 0 ? (
            <div className="text-muted-foreground p-6 text-sm">
              {isFiltered
                ? "No payers match."
                : "No payer owes anything on credit account."}
            </div>
          ) : (
            <div
              aria-busy={isLoading}
              className={cn("transition-opacity", isLoading && "opacity-60")}
            >
              <AgedDebtPayerTable
                payers={list.items}
                totals={list.totals}
                totalPayers={list.total}
                isFiltered={isFiltered}
                openPayerRef={openPayer ? openPayer.billing_account_ref : null}
                onTogglePayer={(payer) =>
                  setOpenRef((current) =>
                    current === payer.billing_account_ref
                      ? null
                      : payer.billing_account_ref,
                  )
                }
                panel={
                  openPayer && (
                    <AgedDebtInvoicesPanel
                      payer={openPayer}
                      reloadKey={reloads}
                      onClose={() => setOpenRef(null)}
                      onDisputesChanged={onDisputesChanged}
                    />
                  )
                }
              />
              <Pager
                summary={`Sorted by ${sortLabel.toLowerCase()}. ${list.items.length} of ${list.total}.`}
                list={list}
                onPageChange={(next) =>
                  setFilter("page", next > 1 ? String(next) : null)
                }
              />
              <p className="text-muted-foreground px-6 pb-4 text-xs">
                Disputed is part of the amounts in the buckets, not an extra
                bucket.
              </p>
            </div>
          ))}
      </div>
    </>
  );
}

// ── Disputes tab ──────────────────────────────────────────────────────────

function DisputesTabBody({
  reloads,
  onDisputesChanged,
  onAsOf,
}: {
  reloads: number;
  onDisputesChanged: () => void;
  onAsOf: (asOf: string) => void;
}) {
  const adapter = useDataAdapter();
  const { params, setFilter, page } = useUrlFilters();
  const q = params.get("q") || undefined;
  const status: DisputeStatus | undefined = oneOf(
    STATUS_OPTIONS,
    params.get("status"),
  );
  const sort: DisputeSort | undefined = oneOf(
    DISPUTE_SORT_OPTIONS,
    params.get("sort"),
  );
  const isFiltered = Boolean(q || status);
  const [query, setQuery] = useSearchBox(q, setFilter);
  const [resolving, setResolving] = useState<DisputeRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { list, isLoading, error } = useServerList<DisputeList>(
    () => adapter.listDisputes({ q, status, sort, page, pageSize: PAGE_SIZE }),
    [adapter, q, status, sort, page, reloads],
    "The disputes did not load.",
  );
  useEffect(() => {
    if (list) onAsOf(list.asOf);
  }, [list, onAsOf]);
  const sortLabel =
    DISPUTE_SORT_OPTIONS.find((o) => o.value === (sort ?? "-raised_at"))
      ?.label ?? "Newest first";

  return (
    <>
      <FilterBar>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Invoice, payer or customer"
          aria-label="Search disputes"
          className="h-9 w-full sm:w-72"
        />
        <select
          aria-label="Status"
          value={status ?? ""}
          onChange={(e) => setFilter("status", e.target.value || null)}
          className={selectClass}
        >
          <option value="">All</option>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Sort disputes"
          value={sort ?? ""}
          onChange={(e) => setFilter("sort", e.target.value || null)}
          className={cn(selectClass, "sm:ml-auto")}
        >
          <option value="">Sort…</option>
          {DISPUTE_SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </FilterBar>

      {notice && (
        <p role="status" className="text-muted-foreground px-6 pt-3 text-sm">
          {notice}
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error && (
          <p role="alert" className="p-6 text-sm text-red-600">
            Could not load the disputes: {error}
          </p>
        )}
        {!list && isLoading && <Loading label="Loading the disputes" />}
        {list &&
          (list.items.length === 0 ? (
            <div className="text-muted-foreground p-6 text-sm">
              {isFiltered
                ? "No disputes match."
                : "No dispute has been recorded."}
            </div>
          ) : (
            <div
              aria-busy={isLoading}
              className={cn("transition-opacity", isLoading && "opacity-60")}
            >
              <DisputesTable rows={list.items} onResolve={setResolving} />
              <Pager
                summary={`${sortLabel}. ${list.items.length} of ${list.total}.`}
                list={list}
                onPageChange={(next) =>
                  setFilter("page", next > 1 ? String(next) : null)
                }
              />
            </div>
          ))}
      </div>

      {resolving && (
        <ResolveDisputeDialog
          dispute={{
            invoiceNo: resolving.invoice_no,
            amountPence: null,
            raisedAt: resolving.raised_at,
            raisedByName: resolving.raised_by_name,
            reason: resolving.reason,
          }}
          onClose={() => setResolving(null)}
          onSubmit={async (note) => {
            await adapter.resolveDispute(resolving, { note });
            setNotice(`Dispute resolved on ${resolving.invoice_no}.`);
            onDisputesChanged();
          }}
        />
      )}
    </>
  );
}
